// ─── Guided Setup: counting things across a line ──────────────────────────────
//
// detector_config.object_count, per camera. A line, and what crosses it gets
// counted in each direction.
//
// `purpose` is the question that decides which report the numbers land in, and
// the backend behaves differently for each — footfall applies the people rules
// (staff zones, longer track memory, crossing debounce, occupancy reset on an
// empty area), throughput does not. So it is asked in those terms rather than as
// a field name.
//
// What can be counted is limited by the model, and this panel says so instead of
// offering a "boxes" option that would silently count nothing: the built-in model
// knows people and vehicles (it is COCO-trained), and anything else — cartons,
// sacks, crates — needs a model trained for it, uploaded by an administrator.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, Panel, Choice, SettingRow, Advanced, countChanged, PolygonList,
         useModuleSaver } from './SetupBits';

const DETECTOR = 'object_count';

// COCO ids the built-in model can count without any extra model.
const BUILTIN = [
  { id: 0, label: 'People',      icon: '🚶' },
  { id: 2, label: 'Cars',        icon: '🚗' },
  { id: 7, label: 'Trucks',      icon: '🚚' },
  { id: 5, label: 'Buses',       icon: '🚌' },
  { id: 3, label: 'Motorcycles', icon: '🏍️' },
];

// `def` matches IndustrialObjectCountPage's defaults, which match the detector's.
const ADVANCED = [
  { key: 'conf',  label: 'Confidence', type: 'range', min: 0.1, max: 0.9, step: 0.05, def: 0.35 },
  { key: 'imgsz', label: 'Image size', type: 'int', min: 640, max: 1920, step: 320, def: 640 },
];
const LINE_ADVANCED = [
  { key: 'min_area_frac', label: 'Ignore things smaller than', type: 'range',
    min: 0, max: 0.2, step: 0.005, def: 0.0,
    hint: 'As a fraction of the frame. Useful where something far away crosses the line and should not be counted.' },
  { key: 'max_area_frac', label: 'Ignore things bigger than', type: 'range',
    min: 0.1, max: 1, step: 0.05, def: 1.0 },
];

const newLine = i => ({
  name: `Line ${i + 1}`, class_ids: [0], object: 'person', line: null, invert: false,
  purpose: 'object', staff_zones: [],
  ...Object.fromEntries(LINE_ADVANCED.map(s => [s.key, s.def])),
});

export default function CountingPanel({ cameras, onChanged }) {
  const [cams,   setCams]   = useState(null);
  const [models, setModels] = useState([]);     // uploaded custom models
  const [edit,   setEdit]   = useState(null);   // { idx, model, model_path, conf, imgsz, lines }
  const [li,     setLi]     = useState(0);      // line being edited
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/cameras')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCams(d.cameras || []); })
    .catch(() => setErr('Could not read the cameras.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/ui/models')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setModels(Array.isArray(d?.models) ? d.models : []))
      .catch(() => {});
  }, []);

  const rows = (cams || [])
    .filter(c => cameras.some(x => x.idx === c.idx))
    .map(c => ({
      idx: c.idx,
      name: c.name || `Camera ${c.idx}`,
      on: (c.detectors || []).includes(DETECTOR),
      cfg: (c.detector_config || {})[DETECTOR] || null,
    }));

  const open = r => {
    const saved = r.cfg || {};
    setErr(null);
    setLi(0);
    setEdit({
      idx: r.idx,
      model: saved.model || 'person',
      model_path: saved.model_path || '',
      conf: saved.conf ?? 0.35,
      imgsz: saved.imgsz ?? 640,
      lines: (saved.lines || []).length
        ? saved.lines.map((l, i) => ({ ...newLine(i), ...l,
            class_ids: Array.isArray(l.class_ids) ? l.class_ids
                     : (l.class_id != null ? [l.class_id] : [0]),
            staff_zones: Array.isArray(l.staff_zones) ? l.staff_zones : [] }))
        : [newLine(0)],
    });
  };

  async function save() {
    const payload = {
      model: edit.model, model_path: edit.model_path,
      conf: edit.conf, imgsz: edit.imgsz,
      lines: edit.lines.map((l, i) => ({ ...l, name: (l.name || '').trim() || `Line ${i + 1}` })),
    };
    const cam  = (cams || []).find(c => c.idx === edit.idx);
    const dets = [...new Set([...(cam?.detectors || []), DETECTOR])];
    const d = await run('PUT', `/cameras/${edit.idx}`,
                        { detectors: dets, detector_config: { [DETECTOR]: payload } },
                        'the counting lines');
    if (d) { setEdit(null); await load(); }
  }

  async function remove(idx) {
    if (!window.confirm('Stop counting on this camera? Counts already recorded are kept.')) return;
    const cam  = (cams || []).find(c => c.idx === idx);
    const dets = (cam?.detectors || []).filter(d => d !== DETECTOR);
    if (await run('PUT', `/cameras/${idx}`, { detectors: dets }, 'the change')) await load();
  }

  const patch = (i, p) => setEdit(e => ({
    ...e, lines: e.lines.map((l, n) => (n === i ? { ...l, ...p } : l)),
  }));
  const allDrawn = edit && edit.lines.every(l => (l.line || []).length === 2);

  // Classes the chosen model can count.
  const custom  = edit && edit.model !== 'person'
    ? models.find(m => m.name === edit.model || m.path === edit.model_path)
    : null;
  const classes = custom
    ? (custom.classes || []).map(c => ({ id: c.id, label: c.name, icon: '📦' }))
    : BUILTIN;

  // ── editing one camera ──
  if (edit) {
    const cam  = rows.find(r => r.idx === edit.idx);
    const line = edit.lines[li];
    return (
      <>
        <div className="gs-section-title">Counting on {cam?.name}</div>

        {models.length > 0 && (
          <Field label="Which model?"
                 help="The built-in model knows people and vehicles. A model your administrator uploaded can count whatever it was trained on.">
            <select className="gs-input" value={edit.model}
                    onChange={e => {
                      const m = models.find(x => x.name === e.target.value);
                      setEdit(s => ({ ...s, model: e.target.value,
                                      model_path: m ? (m.path || '') : '' }));
                      patch(li, { class_ids: m ? [(m.classes || [{ id: 0 }])[0].id] : [0] });
                    }}>
              <option value="person">Built in — people and vehicles</option>
              {models.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          </Field>
        )}

        {/* Lines are tabs rather than a long scroll: each one is a separate
            question about a different doorway. */}
        {edit.lines.length > 1 && (
          <div className="gs-chips">
            {edit.lines.map((l, i) => (
              <button key={i} type="button" className={`gs-chip${i === li ? ' on' : ''}`}
                      onClick={() => setLi(i)}>
                {l.name || `Line ${i + 1}`}
                {(l.line || []).length !== 2 && <span className="gs-chip-off">undrawn</span>}
              </button>
            ))}
          </div>
        )}

        <Field label="What is this line called?" help="It names the column in the report.">
          <input className="gs-input" value={line.name} maxLength={40}
                 onChange={e => patch(li, { name: e.target.value })} />
        </Field>

        <Field label="What should it count?">
          <div className="gs-chips">
            {classes.map(cl => (
              <button key={cl.id} type="button"
                      className={`gs-chip${line.class_ids.includes(cl.id) ? ' on' : ''}`}
                      onClick={() => patch(li, {
                        class_ids: line.class_ids.includes(cl.id)
                          ? line.class_ids.filter(x => x !== cl.id)
                          : [...line.class_ids, cl.id],
                        object: cl.id === 0 && !custom ? 'person' : cl.label.toLowerCase(),
                      })}>
                {cl.icon} {cl.label}
              </button>
            ))}
          </div>
        </Field>
        {!custom && (
          <p className="gs-help">
            Counting cartons, sacks or crates needs a model trained on them. An
            administrator uploads one on the Object Intelligence page, and it then appears
            in the list above — the built-in model genuinely cannot see them, so there is
            nothing to switch on here for it.
          </p>
        )}

        <Field label="Which report should this feed?">
          <div className="gs-choices">
            <Choice icon="📈" title="Throughput" sub="How many went past — the Count report"
                    selected={line.purpose === 'object'}
                    onClick={() => patch(li, { purpose: 'object' })} />
            <Choice icon="🚶" title="Footfall" sub="Customers in and out — the Footfall report"
                    selected={line.purpose === 'footfall'}
                    onClick={() => patch(li, { purpose: 'footfall' })} />
            <Choice icon="🚗" title="Vehicles" sub="Vehicles in and out, with number plates — the Vehicle report"
                    selected={line.purpose === 'vehicle'}
                    onClick={() => patch(li, { purpose: 'vehicle', class_ids: [2, 3, 5, 7], object: 'vehicle' })} />
          </div>
        </Field>
        {line.purpose === 'vehicle' && (
          <p className="gs-help">
            Plates are read from the camera’s main stream as each vehicle crosses. Put the
            line where vehicles pass slowly and face the camera, and close enough that the
            plate is clearly legible on the live view. At night, headlights and IR glare
            often make plates unreadable — the report then shows the plate photo without
            text.
          </p>
        )}

        <Field label="Draw the line" help="Tap the two ends. Put it across the opening, not along it.">
          <PatrolZoneEditor cameraIdx={edit.idx} shape="line" value={line.line || []}
                            onChange={p => patch(li, { line: p })} />
        </Field>

        <SettingRow
          spec={{ key: 'invert', label: 'Swap which way counts as “in”', type: 'bool', def: false,
                  hint: 'Check the report after a few crossings — if in and out are the wrong way round, this is the switch.' }}
          value={line.invert} onChange={v => patch(li, { invert: v })} />

        {line.purpose === 'footfall' && (
          <>
            <div className="gs-section-title">Staff areas (optional)</div>
            <p className="gs-help">
              Draw where your own people stand — behind the counter, at the till. Somebody
              whose track sits in a staff area is not counted as a customer, which is what
              stops your staff being the busiest visitors of the day.
            </p>
            <PolygonList cameraIdx={edit.idx} value={line.staff_zones} noun="staff area"
                         onChange={z => patch(li, { staff_zones: z })} />
          </>
        )}

        <Advanced title="Advanced for this line" changed={countChanged(LINE_ADVANCED, line)}>
          {LINE_ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={line[s.key]}
                        onChange={v => patch(li, { [s.key]: v })} />
          ))}
        </Advanced>
        <Advanced title="Advanced for this camera" changed={countChanged(ADVANCED, edit)}>
          {ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={edit[s.key]}
                        onChange={v => setEdit(e => ({ ...e, [s.key]: v }))} />
          ))}
        </Advanced>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary" onClick={save} disabled={busy || !allDrawn}>
            {busy ? <span className="gs-spin" /> : 'Save counting'}
          </button>
          <button className="gs-btn" onClick={() => {
            setEdit(e => ({ ...e, lines: [...e.lines, newLine(e.lines.length)] }));
            setLi(edit.lines.length);
          }}>Add another line</button>
          {edit.lines.length > 1 && (
            <button className="gs-btn ghost" onClick={() => {
              setEdit(e => ({ ...e, lines: e.lines.filter((_, n) => n !== li) }));
              setLi(0);
            }}>Remove this line</button>
          )}
          <button className="gs-btn ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</button>
          {!allDrawn && <span className="gs-foot-note">Every line needs both ends placed</span>}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the list ──
  return (
    <>
      {!cams && !err && (
        <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>
      )}

      {cams && (
        <>
          <p className="gs-help">
            A line across a doorway, a gate or a conveyor. Anything crossing it is counted
            in each direction, and the numbers go to the Count or Footfall report.
          </p>

          {rows.map(r => {
            const lines = r.cfg?.lines || [];
            const drawn = lines.filter(l => (l.line || []).length === 2).length;
            return (
              <div className={`gs-row${r.on && drawn !== lines.length ? ' gs-row-bad' : ''}`} key={r.idx}>
                <span className="gs-row-icon">{!r.on ? '📷' : drawn ? '🔢' : '⚠️'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}</span>
                  <span className="gs-row-sub">
                    {!r.on ? 'Not counting anything'
                      : lines.length === 0 ? 'Switched on with no line — it counts nothing'
                      : `${lines.map(l => l.name).join(', ')}`
                        + (drawn === lines.length ? '' : ` · ${lines.length - drawn} not drawn`)}
                  </span>
                </span>
                <button className="gs-btn small" onClick={() => open(r)}>
                  {r.on ? 'Change' : 'Set one up'}
                </button>
                {r.on && (
                  <button className="gs-btn small ghost" disabled={busy}
                          onClick={() => remove(r.idx)}>Stop</button>
                )}
              </div>
            );
          })}

          <Panel title="Counting costs a model pass per camera">
            This runs its own detection on every camera you put a line on, so put lines
            only where you need numbers. The load figure on the AI models step does not
            include these.
          </Panel>

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
